import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/product/ProductCard';
import ProductFilter from '../components/product/ProductFilter';
import { productAPI } from '../services/api';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { Product, ProductFilters } from '../types';
import './ProductList.css';

const ProductList: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const filters: ProductFilters = {
    category: searchParams.get('category') || undefined,
    price_min: searchParams.get('price_min') ? Number(searchParams.get('price_min')) : undefined,
    price_max: searchParams.get('price_max') ? Number(searchParams.get('price_max')) : undefined,
    search: searchParams.get('search') || undefined,
    ordering: searchParams.get('ordering') || undefined,
    page: currentPage,
  };

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentPage]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getProducts(filters);
      setProducts(response.results);
      setTotalCount(response.count);
      
      // Track Search event when user searches or filters products
      if (filters.search) {
        trackFacebookEvent('Search', {
          search_string: filters.search,
          content_type: 'product',
          content_category: filters.category || 'all'
        });
      } else if (filters.category && response.results && response.results.length > 0) {
        // Track ViewContent for category browsing with valid product IDs
        const categoryProductIds = response.results.slice(0, 10).map((p: any) => p.id.toString());
        trackFacebookEvent('ViewContent', {
          content_name: `Category: ${filters.category}`,
          content_id: categoryProductIds[0],
          content_ids: categoryProductIds,
          content_type: 'product_group',
          content_category: filters.category
        });
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: ProductFilters) => {
    const params = new URLSearchParams();

    // Preserve category from URL if not in newFilters
    const currentCategory = searchParams.get('category');
    if (newFilters.category) {
      params.set('category', newFilters.category);
    } else if (currentCategory) {
      params.set('category', currentCategory);
    }

    if (newFilters.price_min) params.set('price_min', newFilters.price_min.toString());
    if (newFilters.price_max) params.set('price_max', newFilters.price_max.toString());
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.ordering) params.set('ordering', newFilters.ordering);

    setSearchParams(params);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / 20);

  return (
    <div className="product-list-page">
      <div className="container">
        <div className="product-list-layout">
          <aside className="filters-sidebar">
            <ProductFilter currentFilters={filters} onFilterChange={handleFilterChange} />
          </aside>

          <div className="products-main">
            {loading ? (
              <div className="loading-grid">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="skeleton-card"></div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <>
                <div className="products-grid">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← {t('buttons.previous')}
                    </button>
                    <span className="pagination-info">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      {t('buttons.next')} →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-products">
                <p>{t('search.noResults')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductList;
